import React from "react";
import { Box, Card, CardActionArea, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import _ from "lodash";
import BatteryIndicator from "app/widgets/Shared/BatteryIndicator/BatteryIndicator";
import { CARD_MEASUREMENTS, deviceMeasuresField, formatMeasurementValue, formatTimeAgo, isReadingStale } from "./deviceDisplay";

// Tarjeta única de dispositivo: la usan la vista de un local y la lista general. Lo que cambia entre ambas son las
// props, no el aspecto.
const DeviceCard = ({
    device,
    onOpen = null,
    onEditClick = null,
    onAlertClick = null,
    showLocation = false,
    storeName = null,
    zoneName = null,
    dragHandleProps = null,
    ...cardProps
}) => {
    const latest = device?.latest_device_metric;
    const latestTime = latest?.time;
    const stale = isReadingStale(latestTime);
    const measurements = _.keys(CARD_MEASUREMENTS).filter((field) => (
        deviceMeasuresField(device, field) && !(CARD_MEASUREMENTS[field].onlyWithValue && _.isNil(latest?.[field]))
    ));

    const stopPropagation = (handler) => (event) => {
        event.stopPropagation();
        handler?.();
    };

    return (
        <Card
            data-device-card="true"
            variant="outlined"
            sx={{ height: "100%", borderRadius: 2.5, transition: "border-color 150ms ease, box-shadow 150ms ease",
                  "&:hover": { borderColor: "primary.main", boxShadow: "0 8px 20px rgba(15, 23, 42, 0.07)" } }}
            {...cardProps}>
            <CardActionArea
                onClick={onOpen ? () => onOpen(device) : undefined}
                disableRipple={!onOpen}
                component="div"
                sx={{ height: "100%", p: 1.5, alignItems: "stretch", cursor: onOpen ? "pointer" : "default" }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        {showLocation && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", fontWeight: 500 }}>
                                {(storeName || "Local sin nombre")} &gt; {(zoneName || "Sin zona")}
                            </Typography>
                        )}
                        <Typography variant="h5" sx={{ fontWeight: 600 }} noWrap>{device?.name || "Sin nombre"}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                            {_.compact([device?.kind, device?.code]).join(" · ")}
                        </Typography>
                    </Box>

                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                        {dragHandleProps && (
                            <Tooltip title="Arrastrar para cambiar de zona">
                                <Box
                                    {...dragHandleProps}
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{ display: "flex", alignItems: "center", p: 0.4, cursor: "grab",
                                          color: "text.disabled", "&:active": { cursor: "grabbing" } }}>
                                    <DragIndicatorOutlinedIcon fontSize="small" />
                                </Box>
                            </Tooltip>
                        )}
                        {onAlertClick && (
                            <Tooltip title="Sus automatizaciones">
                                <IconButton size="small" onClick={stopPropagation(() => onAlertClick(device))}>
                                    <NotificationsActiveOutlinedIcon color="energy_off" fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onEditClick && (
                            <Tooltip title="Editar dispositivo">
                                <IconButton size="small" onClick={stopPropagation(() => onEditClick(device?.id))}>
                                    <SettingsOutlinedIcon color="energy_off" fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.85} sx={{ minWidth: 0, flexWrap: "wrap" }} useFlexGap>
                        <BatteryIndicator
                            level={latest?.battery}
                            batteryOrPlug={_.isNil(latest?.battery) ? "plug" : "battery"}
                        />
                        {measurements.map((field) => {
                            const config = CARD_MEASUREMENTS[field];
                            const value = latest?.[field];
                            return (
                                <Tooltip key={field} title={config.title || ""}>
                                    <Stack direction="row" alignItems="center" spacing={0.15}>
                                        <config.icon fontSize="small" color={value === "OFF" ? "energy_off" : config.color} />
                                        <Typography variant="caption" color="text.primary">
                                            {formatMeasurementValue(value, config)}
                                        </Typography>
                                    </Stack>
                                </Tooltip>
                            );
                        })}
                    </Stack>
                    <Typography variant="caption" color={stale ? "warning.main" : "text.secondary"} sx={{ flexShrink: 0 }}>
                        {latestTime ? formatTimeAgo(latestTime) : "sin datos"}
                    </Typography>
                </Stack>
            </CardActionArea>
        </Card>
    );
};

export default DeviceCard;
